import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { authService } from '../services/authService';
import { LoginRequest } from '../types/auth';
import { getDashboardPath } from '../utils/roleRouting';
import { ErrorType } from '../types/errors';
import DismissibleError from './DismissibleError';

declare global {
  interface Window {
    grecaptcha?: {
      render: (
        container: HTMLElement,
        params: {
          sitekey?: string;
          callback?: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        }
      ) => number;
      reset?: (widgetId?: number) => void;
      getResponse?: (widgetId?: number) => string;
    };
  }
}

function waitForRecaptchaReady(
  resolve: () => void,
  reject: (error: Error) => void
): void {
  let attempts = 0;

  const readinessCheck = window.setInterval(() => {
    attempts += 1;

    if (window.grecaptcha && typeof window.grecaptcha.render === 'function') {
      window.clearInterval(readinessCheck);
      resolve();
      return;
    }

    if (attempts >= 100) {
      window.clearInterval(readinessCheck);
      reject(new Error('reCAPTCHA API loaded but grecaptcha.render was not ready.'));
    }
  }, 50);
}

function loadRecaptchaScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.grecaptcha && typeof window.grecaptcha.render === 'function') {
      resolve();
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[src*="www.google.com/recaptcha/api.js"]'
    );

    if (existingScript) {
      waitForRecaptchaReady(resolve, reject);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      waitForRecaptchaReady(resolve, reject);
    };
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA script'));
    document.head.appendChild(script);
  });
}

interface LoginProps {
  initialError?: string;
}

const Login: React.FC<LoginProps> = ({ initialError = '' }) => {
  const [credentials, setCredentials] = useState<LoginRequest>({ username: '', password: '' });
  const [error, setError] = useState(initialError);
  const [loading, setLoading] = useState(false);
  const [recaptchaRequired, setRecaptchaRequired] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [provisioningUri, setProvisioningUri] = useState('');
  const [recaptchaContainer, setRecaptchaContainer] = useState<HTMLDivElement | null>(null);
  const recaptchaWidgetId = useRef<number | null>(null);
  const submittingRef = useRef(false);
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

  useEffect(() => {
    if (!recaptchaRequired) return undefined;
    if (!recaptchaContainer) return undefined;

    let cancelled = false;

    const renderRecaptcha = async () => {
      if (cancelled || recaptchaWidgetId.current !== null) return;

      if (!siteKey) {
        setError('reCAPTCHA site key is not configured.');
        return;
      }

      if (!recaptchaContainer.isConnected) {
        setError('reCAPTCHA container failed to load. Please refresh the page and try again.');
        return;
      }

      try {
        await loadRecaptchaScript();

        if (cancelled || !recaptchaContainer.isConnected || recaptchaWidgetId.current !== null) return;
        if (!window.grecaptcha || typeof window.grecaptcha.render !== 'function') {
          setError('reCAPTCHA failed to load. Please refresh the page and try again.');
          return;
        }

        recaptchaWidgetId.current = window.grecaptcha.render(recaptchaContainer, {
          sitekey: siteKey,
          callback: (token: string) => {
            setCredentials((current) => ({ ...current, recaptcha_token: token }));
            setError('');
          },
          'expired-callback': () => {
            setCredentials((current) => ({ ...current, recaptcha_token: '' }));
          },
          'error-callback': () => {
            setCredentials((current) => ({ ...current, recaptcha_token: '' }));
            setError('reCAPTCHA encountered an error. Please try again.');
          },
        });
      } catch (renderError) {
        console.error('reCAPTCHA render failed', renderError);
        setError('reCAPTCHA failed to load. Please refresh the page and try again.');
      }
    };

    renderRecaptcha();

    return () => {
      cancelled = true;
    };
  }, [recaptchaRequired, recaptchaContainer, siteKey]);

  useEffect(() => {
    if (recaptchaRequired) return;
    recaptchaWidgetId.current = null;
    setCredentials((current) => ({ ...current, recaptcha_token: '' }));
  }, [recaptchaRequired]);

  const resetRecaptcha = () => {
    if (window.grecaptcha?.reset && recaptchaWidgetId.current !== null) {
      window.grecaptcha.reset(recaptchaWidgetId.current);
    }
    setCredentials((current) => ({ ...current, recaptcha_token: '' }));
  };

  const redirectAfterAuthentication = (user: any, redirectUrl?: string) => {
    window.location.href = redirectUrl || getDashboardPath(user);
  };

  const messageForLoginError = (loginError: unknown) => {
    const apiError = loginError as { message?: string; type?: ErrorType; statusCode?: number };
    if (apiError?.type === ErrorType.NETWORK) {
      return 'Unable to reach the CyberShield server.';
    }
    if (apiError?.type === ErrorType.SERVER || (apiError?.statusCode && apiError.statusCode >= 500)) {
      return 'The CyberShield server did not respond in time.';
    }
    return apiError?.message || 'CyberShield could not complete authentication. Please try again.';
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setCredentials((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    setError('');
    try {
      if (!credentials.username.trim() || !credentials.password) {
        setError('Username or official email and password are required.');
        return;
      }
      if (recaptchaRequired && !credentials.recaptcha_token) {
        setError('Please complete the reCAPTCHA challenge.');
        return;
      }
      const response = await authService.login(credentials);
      if (response.mfa_required) {
        setMfaRequired(true);
        if (response.mfa_setup_required) {
          const setup = await authService.getMfaSetup();
          setProvisioningUri(setup.provisioning_uri || '');
        }
      } else if (response.success) {
        redirectAfterAuthentication(response.user, response.redirect_url);
      } else {
        setError(response.error || 'Login failed. Check your credentials and try again.');
        setRecaptchaRequired(Boolean(response.recaptcha_required));
        if (response.recaptcha_required) resetRecaptcha();
      }
    } catch (error: unknown) {
      setError(messageForLoginError(error));
      if (recaptchaRequired) resetRecaptcha();
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    setError('');
    try {
      const response = await authService.verifyMfa(mfaCode);
      if (response.success) {
        redirectAfterAuthentication(response.user, response.redirect_url);
      }
      else setError(response.error || 'The authenticator code is invalid or expired.');
    } catch (error: unknown) {
      const apiError = error as { message?: string; type?: ErrorType; statusCode?: number } | undefined;
      if (
        apiError?.type === ErrorType.AUTHENTICATION
        || apiError?.statusCode === 400
        || apiError?.statusCode === 401
      ) {
        setError('The authenticator code is invalid or expired.');
      } else {
        setError(messageForLoginError(error));
      }
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <main className="login-page original-login">
      <section className="login-card" aria-labelledby="login-title">
        <img className="login-logo" src="/cybershield-logo.png" alt="CyberShield" />
        <h1 id="login-title">{mfaRequired ? 'Verify your identity' : 'Sign in to CyberShield'}</h1>
        <p className="form-intro">{mfaRequired ? 'Open Microsoft Authenticator or Google Authenticator, then enter the current six-digit code.' : 'Use your official email address or assigned username.'}</p>
        {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
        <form onSubmit={mfaRequired ? handleMfaSubmit : handleSubmit}>
          {mfaRequired ? (
            <>
              {provisioningUri && (
                <div className="info-message mfa-setup-panel">
                  <p>Scan this QR code using Microsoft Authenticator, Google Authenticator, or another TOTP authenticator app.</p>
                  <div className="mfa-qr-wrapper">
                    <QRCodeSVG value={provisioningUri} size={180} />
                  </div>
                  <details>
                    <summary>Manual setup key</summary>
                    <code>{provisioningUri}</code>
                  </details>
                </div>
              )}
              <div className="form-group"><label htmlFor="mfa-code">Authenticator code</label><input id="mfa-code" value={mfaCode} onChange={(event) => setMfaCode(event.target.value)} inputMode="numeric" autoComplete="one-time-code" required /></div>
            </>
          ) : (
            <>
              <div className="form-group"><label htmlFor="username">Username</label><input id="username" type="text" name="username" value={credentials.username} onChange={handleInputChange} placeholder="Enter your username" autoComplete="username" required /></div>
              {recaptchaRequired && (
                <div className="form-group">
                  <label>reCAPTCHA verification</label>
                  {siteKey ? (
                    <div ref={setRecaptchaContainer} className="recaptcha-widget-container" />
                  ) : (
                    <div className="error-message" role="alert">
                      reCAPTCHA site key is not configured.
                    </div>
                  )}
                </div>
              )}
              <div className="form-group"><label htmlFor="password">Password</label><input id="password" type="password" name="password" value={credentials.password} onChange={handleInputChange} placeholder="Enter your password" autoComplete="current-password" required /></div>
            </>
          )}
          <button className="btn btn-primary login-submit" type="submit" disabled={loading || (recaptchaRequired && !credentials.recaptcha_token)}>{loading ? 'Please wait…' : (mfaRequired ? 'Verify' : 'Login')}</button>
        </form>
        {!mfaRequired && <p className="login-footer"><Link to="/forgot-password">Forgot password?</Link></p>}
        <p className="login-footer"><strong>CyberShield:</strong> A Centralized Firearm Traceability and Accountability Platform<br />Authorized personnel only.<br />© 2026 CyberShield. All rights reserved.</p>
      </section>
    </main>
  );
};

export default Login;
